import { Module } from 'vuex';

import { accountingMappingsService } from '@/services/accounting-mappings/accounting-mappings.service';
import { AccountingMapping, AccountingMappingPayload } from '@/services/accounting-mappings/accounting-mappings.types';
import { RootState } from '@/store';

export interface AccountingMappingState {
  accountingMapping: AccountingMapping;
  accountingMappingLoading: boolean;
  commissionTypes: string[];
  commissionTypesLoading: boolean;
}

const state: AccountingMappingState = {
  accountingMapping: null,
  accountingMappingLoading: false,
  commissionTypes: [],
  commissionTypesLoading: false
};

const options: Module<AccountingMappingState, RootState> = {
  namespaced: true,
  state: () => state,
  actions: {
    setAccountingMapping: (
      { commit, dispatch },
      accountingMapping: AccountingMapping | string | number
    ): Promise<void> => {
      if (typeof accountingMapping === 'number' || typeof accountingMapping === 'string') {
        return dispatch('getAccountingMappingById', accountingMapping);
      }
      commit('accountingMapping', accountingMapping);
      if (accountingMapping && (accountingMapping as AccountingMapping).id) {
        return dispatch('getAccountingMappingById', (accountingMapping as AccountingMapping).id);
      }
    },
    getAccountingMappingById: ({ commit, dispatch }, id: number): Promise<void> => {
      commit('accountingMappingLoading', true);
      return accountingMappingsService
        .get(id)
        .then(mapping => mapping && commit('accountingMapping', mapping))
        .catch(error => dispatch('alert/pushError', error, { root: true }))
        .finally(() => commit('accountingMappingLoading', false));
    },
    newAccountingMapping: ({ commit, dispatch }): Promise<void> => {
      commit('accountingMappingLoading', true);
      return accountingMappingsService
        .newMapping()
        .then(mapping => mapping && commit('accountingMapping', mapping))
        .catch(error => dispatch('alert/pushError', error, { root: true }))
        .finally(() => commit('accountingMappingLoading', false));
    },
    createAccountingMapping: ({ commit, dispatch }, payload: AccountingMappingPayload): Promise<void> => {
      commit('accountingMappingLoading', true);
      return accountingMappingsService
        .create(payload)
        .then(mapping => mapping && commit('accountingMapping', mapping))
        .then(() => dispatch('alert/pushSuccess', 'Libellé ajouté !', { root: true }))
        .catch(error => dispatch('alert/pushError', error, { root: true }))
        .finally(() => commit('accountingMappingLoading', false));
    },
    updateAccountingMapping: (
      { commit, dispatch, state },
      payload: Partial<AccountingMappingPayload>
    ): Promise<void> => {
      if (!state.accountingMapping?.id) return Promise.resolve();
      commit('accountingMappingLoading', true);
      return accountingMappingsService
        .update(state.accountingMapping.id, payload)
        .then(mapping => mapping && commit('accountingMapping', mapping))
        .then(() => dispatch('alert/pushSuccess', 'Libellé mis à jour !', { root: true }))
        .catch(error => dispatch('alert/pushError', error, { root: true }))
        .finally(() => commit('accountingMappingLoading', false));
    },
    clearAccountingMapping: ({ dispatch }): Promise<void> => {
      return dispatch('setAccountingMapping', null);
    },
    getCommissionTypes: ({ commit, dispatch, state }): Promise<void> => {
      if (state.commissionTypes.length > 0 || state.commissionTypesLoading) return Promise.resolve();
      commit('commissionTypesLoading', true);
      return accountingMappingsService
        .commissionTypes()
        .then(types => commit('commissionTypes', types || []))
        .catch(error => dispatch('alert/pushError', error, { root: true }))
        .finally(() => commit('commissionTypesLoading', false));
    }
  },
  mutations: {
    accountingMapping: (state, mapping: AccountingMapping) => (state.accountingMapping = mapping),
    accountingMappingLoading: (state, loading: boolean) => (state.accountingMappingLoading = loading),
    commissionTypes: (state, types: string[]) => (state.commissionTypes = types),
    commissionTypesLoading: (state, loading: boolean) => (state.commissionTypesLoading = loading)
  }
};

export default options;
